import {
    Component,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { CalendarEventType } from "./interface";
import { getCalendarSheet, getMonthNames, getDayNames } from "./utils";

/** static base class */
const baseClass = "ux-iws-event-calendar-sheet";

@Component({
    tag: "ux-iws-event-calendar-sheet",
    styleUrl: "event-calendar-sheet.scss",
})
export class EventCalendarSheet {
    /** Set this flag if the calendar should only render the navigation header */
    @Prop() compact: boolean = false;
    /** The currently active month (0-11) */
    @Prop({ mutable: true, reflect: true }) activeMonth: number;
    /** The currently active year (YYYY) */
    @Prop({ mutable: true, reflect: true }) activeYear: number;
    /** Events used to highlight days and restrict date navigation */
    @Prop() events: CalendarEventType[] = [];
    /** Language used to localize texts  */
    @Prop() locale: string = "";

    @State() minDate: Date | null = null;
    @State() maxDate: Date | null = null;

    /** Emitted when a date is selected  */
    @Event({ eventName: "uxChange", bubbles: true, cancelable: true })
    dateChange: EventEmitter<string>;

    @Watch("activeMonth")
    @Watch("activeYear")
    setActiveDate() {
        this.dateChange.emit(
            new Date(this.activeYear, this.activeMonth, 1, 12)
                .toISOString()
                .split("T")[0]
        );
    }

    @Watch("events")
    setEvents() {
        if (this.events.length === 0) {
            this.minDate = null;
            this.maxDate = null;
        } else {
            const earliestEvent = this.events.reduce(
                (previousValue, currentValue) => {
                    if (
                        new Date(currentValue.startDate).getTime() <
                        new Date(previousValue.startDate).getTime()
                    ) {
                        return currentValue;
                    }
                    return previousValue;
                }
            );
            const latestEvent = this.events.reduce(
                (previousValue, currentValue) => {
                    if (
                        new Date(currentValue.endDate).getTime() >
                        new Date(previousValue.endDate).getTime()
                    ) {
                        return currentValue;
                    }
                    return previousValue;
                }
            );
            this.minDate = new Date(earliestEvent.startDate);
            this.maxDate = new Date(latestEvent.endDate);
        }
    }

    componentWillLoad() {
        const now = new Date();
        if (this.activeYear === undefined) {
            this.activeYear = now.getFullYear();
        }
        if (this.activeMonth === undefined) {
            this.activeMonth = now.getMonth();
        }
    }

    private stepActiveYear = (step: number) => {
        this.activeYear += step;
    };

    private stepActiveMonth = (step: number) => {
        let nextMonth: number = this.activeMonth + step;
        if (nextMonth > 11) {
            this.activeYear++;
            nextMonth -= 12;
        } else if (nextMonth < 0) {
            this.activeYear--;
            nextMonth += 12;
        }
        this.activeMonth = nextMonth;
    };

    private getIsDayHighlighted = (day: string): boolean => {
        for (let i = 0; i < this.events.length; i++) {
            let dateDay = new Date(day);
            dateDay.setHours(12, 0, 0);
            let dateStart = new Date(this.events[i].startDate);
            dateStart.setHours(0, 0, 0);
            let dateEnd = new Date(this.events[i].endDate);
            dateEnd.setHours(23, 59, 59);
            if (
                dateDay.getTime() >= dateStart.getTime() &&
                dateDay.getTime() <= dateEnd.getTime()
            ) {
                return true;
            }
        }
        return false;
    };

    private renderDay = (day: string) => {
        const now = new Date();
        const dayDate = new Date(day);
        const isToday =
            dayDate.toISOString().split("T")[0] ===
            now.toISOString().split("T")[0];
        const isSameMonth = dayDate.getMonth() === this.activeMonth;
        const isHighlighted = this.getIsDayHighlighted(day);
        return (
            <div
                key={day}
                class={{
                    [`${baseClass}__day`]: true,
                    [`${baseClass}__day--today`]: isToday,
                    [`${baseClass}__day--inactive`]: !isSameMonth,
                    [`${baseClass}__day--highlighted`]: isHighlighted,
                }}
            >
                <span class={`${baseClass}__day__label`}>
                    {parseInt(day.split("-").pop())}
                </span>
            </div>
        );
    };

    render() {
        const lng =
            this.locale.length >= 2 ? this.locale.slice(0, 2) : undefined;
        const dayNames = getDayNames(lng, "short", 1);
        const monthNames = getMonthNames(lng, "short");
        const sheet = getCalendarSheet(
            this.activeYear,
            this.activeMonth,
            undefined,
            lng
        );

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--compact`]: this.compact,
                }}
            >
                <div class={`${baseClass}__header`}>
                    <button
                        type="button"
                        onClick={() => this.stepActiveYear(-1)}
                    >
                        <ux-icon name="chevron-left-double" weight="bold" />
                    </button>
                    <button
                        type="button"
                        onClick={() => this.stepActiveMonth(-1)}
                    >
                        <ux-icon name="chevron-left" weight="bold" />
                    </button>
                    <span class={`${baseClass}__title`}>
                        <span class="ux-label">
                            {monthNames[this.activeMonth]} {this.activeYear}
                        </span>
                    </span>
                    <button
                        type="button"
                        onClick={() => this.stepActiveMonth(1)}
                    >
                        <ux-icon name="chevron-right" weight="bold" />
                    </button>
                    <button
                        type="button"
                        onClick={() => this.stepActiveYear(1)}
                    >
                        <ux-icon name="chevron-right-double" weight="bold" />
                    </button>
                </div>
                <div class={`${baseClass}__day-names`}>
                    {dayNames.map((dayName) => (
                        <div key={dayName} class={`${baseClass}__day`}>
                            <span class={`${baseClass}__day__label`}>
                                {dayName}
                            </span>
                        </div>
                    ))}
                </div>
                <div class={`${baseClass}__days`}>
                    {sheet.days.map(this.renderDay)}
                </div>
                <div class={`${baseClass}__slot`}>
                    <slot />
                </div>
            </Host>
        );
    }
}
